import { useState, useEffect, useMemo, Fragment } from 'react'
import { useQueryState, parseAsStringLiteral } from 'nuqs'
import { useRouter } from '../router'
import {
  FiExternalLink,
  FiCopy,
  FiCheck,
  FiTerminal,
  FiSearch,
  FiX,
  FiGithub,
  FiArrowUpRight,
  FiBriefcase
} from 'react-icons/fi'
import { getAllProjectDetails } from '../utils/projects'


const typeNames = {
  all: 'All Projects',
  ml: 'Machine Learning',
  cc: 'Coding Challenges',
  fed: 'Frontend Development',
  bed: 'Full Stack',
  ld: 'Design'
}

// Full-time stints — rendered as a card in timeline gaps where side projects paused
const workStints = [
  {
    company: 'Driffle',
    role: 'Founding Engineer',
    url: 'https://driffle.com',
    start: 2021,
    end: 2025
  }
]

const filterTags = [
  { id: 'all', label: 'all' },
  { id: 'ml', label: 'machine learning' },
  { id: 'bed', label: 'full stack' },
  { id: 'fed', label: 'frontend' },
  { id: 'cc', label: 'coding challenges' },
  { id: 'ld', label: 'design' }
]

// Valid filter values for type safety
const filterValues = ['all', 'ml', 'bed', 'fed', 'cc', 'ld']

const installCommands = [
  {
    id: 'loop',
    label: 'loop',
    kind: 'curl',
    repo: 'notshekhar/loop',
    command: 'curl -fsSL https://raw.githubusercontent.com/notshekhar/loop/main/install.sh | bash',
    github: 'https://github.com/notshekhar/loop',
  },
  {
    id: 'digg',
    label: 'digg',
    kind: 'curl',
    repo: 'notshekhar/digg',
    command: 'curl -fsSL https://raw.githubusercontent.com/notshekhar/digg/main/install.sh | bash',
    github: 'https://github.com/notshekhar/digg',
  },
  {
    id: 'markdown',
    label: 'markdown',
    kind: 'curl',
    repo: 'notshekhar/markdown',
    command: 'curl -fsSL https://raw.githubusercontent.com/notshekhar/markdown/main/install.sh | bash',
    github: 'https://github.com/notshekhar/markdown',
  },
  {
    id: 'sqlbot',
    label: 'sqlbot',
    kind: 'npm',
    command: 'npm i -g sqlbot',
    github: 'https://github.com/notshekhar/sqlbot',
  },
]

function Projects() {
  const [projects, setProjects] = useState([])
  const [copiedId, setCopiedId] = useState(null)
  const [search, setSearch] = useState('')
  const [currentFilter, setCurrentFilter] = useQueryState(
    'tab',
    parseAsStringLiteral(filterValues).withDefault('all')
  )

  const handleCopy = async (id, command) => {
    try {
      await navigator.clipboard.writeText(command)
      setCopiedId(id)
      setTimeout(() => setCopiedId(prev => (prev === id ? null : prev)), 1800)
    } catch {
      setCopiedId(null)
    }
  }

  useEffect(() => {
    setProjects(getAllProjectDetails())
  }, [])

  const allProjects = useMemo(() => {
    return projects.map(p => ({
      ...p,
      year: new Date(p.date).getFullYear().toString(),
      url: p.github
    }))
  }, [projects])

  const featuredProjects = useMemo(() => allProjects.slice(0, 3), [allProjects])

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase()
    return allProjects.filter(p => {
      if (currentFilter !== 'all' && p.type !== currentFilter) return false
      if (!q) return true
      const haystack = [
        p.title,
        p.description,
        ...(p.technologies || [])
      ].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [allProjects, currentFilter, search])

  const groupedProjects = useMemo(() => {
    return filteredProjects.reduce((acc, project) => {
      if (!acc[project.year]) acc[project.year] = []
      acc[project.year].push(project)
      return acc
    }, {})
  }, [filteredProjects])

  const sortedYears = useMemo(() => {
    return Object.keys(groupedProjects).sort((a, b) => b - a)
  }, [groupedProjects])

  const totalProjects = allProjects.length
  const latestYear = useMemo(() => {
    const years = allProjects
      .map(p => new Date(p.updated || p.date).getFullYear())
      .filter(y => !Number.isNaN(y))
    return years.length ? Math.max(...years) : new Date().getFullYear()
  }, [allProjects])
  const totalRounded = Math.max(10, Math.floor(totalProjects / 10) * 10)

  return (
    <section id="projects" className="section active">
      <div className="model-header">
        <h1 className="model-title">Shekhar Tyagi</h1>
        <p className="model-description">
          Building at the intersection of machine learning and the web — from neural nets in the browser
          to local-first AI tools. Making complex ideas interactive, useful, and a little fun.
        </p>
        <p className="hero-meta">
          {totalRounded}+ open-source projects · shipping since 2017 · last updated {latestYear}
        </p>
      </div>

      <div className="code-block">
        <div className="code-header">
          <div className="code-dots" aria-hidden="true">
            <span className="code-dot code-dot-red"></span>
            <span className="code-dot code-dot-yellow"></span>
            <span className="code-dot code-dot-green"></span>
          </div>
          <div className="code-title">
            <FiTerminal size={13} />
            <span>zsh — install</span>
          </div>
        </div>
        <div className="code-content code-content-commands">
          <div className="code-comment"># tools I ship — pipe to bash responsibly</div>
          {installCommands.map(item => {
            const isCopied = copiedId === item.id
            return (
              <div key={item.id} className="code-command-row">
                {/* Visible text = exact install command (safe for select-to-copy).
                    Package/repo name still links to GitHub. */}
                <pre className="code-line">
                  <span className="code-prompt">$</span>
                  {item.kind === 'curl' ? (
                    <>
                      <span className="code-cmd">curl</span>
                      <span className="code-flag">-fsSL</span>
                      <span className="code-url">
                        https://raw.githubusercontent.com/
                        <a
                          href={item.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="code-link"
                          title={`Open ${item.label} on GitHub`}
                        >
                          {item.repo}
                        </a>
                        /main/install.sh
                      </span>
                      <span className="code-flag">| bash</span>
                    </>
                  ) : (
                    <>
                      <span className="code-cmd">npm</span>
                      <span className="code-flag">i -g</span>
                      <a
                        href={item.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="code-link"
                        title={`Open ${item.label} on GitHub`}
                      >
                        {item.label}
                      </a>
                    </>
                  )}
                </pre>
                <button
                  type="button"
                  className={`code-copy code-copy-inline ${isCopied ? 'copied' : ''}`}
                  onClick={() => handleCopy(item.id, item.command)}
                  aria-label={isCopied ? `Copied ${item.label}` : `Copy ${item.label} install command`}
                  title={isCopied ? 'Copied!' : `Copy ${item.label}`}
                >
                  {isCopied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {featuredProjects.length > 0 && !search && currentFilter === 'all' && (
        <div className="featured-section">
          <div className="featured-header">
            <h2 className="featured-title">Featured</h2>
          </div>
          <div className="featured-grid">
            {featuredProjects.map(project => (
              <FeaturedCard key={project.slug} project={project} />
            ))}
          </div>
        </div>
      )}

      <div className="projects-toolbar">
        <div className="projects-search">
          <FiSearch size={16} className="projects-search-icon" aria-hidden="true" />
          <input
            type="search"
            className="projects-search-input"
            placeholder="Search projects, tech, keywords…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search projects"
          />
          {search && (
            <button
              type="button"
              className="projects-search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              <FiX size={14} />
            </button>
          )}
        </div>

        <div className="filter-tabs" role="tablist" aria-label="Filter projects by category">
          {filterTags.map(tag => {
            const isActive = currentFilter === tag.id
            return (
              <button
                key={tag.id}
                role="tab"
                aria-selected={isActive}
                className={`filter-tab ${isActive ? 'active' : ''}`}
                onClick={() => setCurrentFilter(tag.id)}
              >
                {tag.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="projects-results-meta">
        <span>
          Showing <strong>{filteredProjects.length}</strong>
          {filteredProjects.length === 1 ? ' project' : ' projects'}
          {search ? ` for “${search.trim()}”` : ''}
        </span>
      </div>

      <div className="projects-list">
        {sortedYears.length === 0 ? (
          <div className="empty-state">
            <p>No projects match your filters.</p>
          </div>
        ) : (
          sortedYears.map((year, yi) => {
            const nextYear = sortedYears[yi + 1]
            // Gap year(s) between this and next section covered by a work stint?
            const stint = nextYear && +year - +nextYear > 1
              ? workStints.find(s => s.start <= +year - 1 && s.end >= +nextYear + 1)
              : null
            return (
              <Fragment key={year}>
                <div className="year-section">
                  <div className="year-header">
                    <span className="year-label">{year}</span>
                    <span className="year-count">
                      {groupedProjects[year].length} {groupedProjects[year].length === 1 ? 'project' : 'projects'}
                    </span>
                  </div>
                  {groupedProjects[year].map((project, index) => (
                    <ProjectCard key={`${year}-${project.slug || index}`} project={project} />
                  ))}
                </div>
                {stint && <WorkStint stint={stint} />}
              </Fragment>
            )
          })
        )}
      </div>
    </section>
  )
}

function WorkStint({ stint }) {
  return (
    <div className="work-stint">
      <div className="work-stint-icon" aria-hidden="true">
        <FiBriefcase size={14} />
      </div>
      <div className="work-stint-body">
        <a
          href={stint.url}
          target="_blank"
          rel="noopener noreferrer"
          className="work-stint-company"
        >
          {stint.company}
        </a>
        <span className="work-stint-years">{stint.start}–{stint.end}</span>
        <span className="work-stint-note">
          {stint.role} · full-time · fewer side projects these years
        </span>
      </div>
    </div>
  )
}

function FeaturedCard({ project }) {
  const { navigate } = useRouter()
  const typeName = typeNames[project.type] || typeNames.all
  const primaryHref = project.demo || project.url
  const desc = project.description
    || (project.technologies?.length
      ? `${typeName} · ${project.technologies.slice(0, 3).join(', ')}`
      : typeName)

  return (
    <article className="featured-card">
      <div className="featured-card-head">
        <h3 className="featured-card-title">{project.title}</h3>
        <span className="featured-card-year">{project.year}</span>
      </div>
      <p className="featured-card-desc">{desc}</p>
      <div className="featured-card-footer">
        {project.detailsIncluded ? (
          <a
            href={`/?p=project&slug=${project.slug}`}
            onClick={e => { e.preventDefault(); navigate('project', project.slug) }}
            className="featured-card-link primary"
          >
            Details <FiArrowUpRight size={12} />
          </a>
        ) : primaryHref ? (
          <a
            href={primaryHref}
            target="_blank"
            rel="noopener noreferrer"
            className="featured-card-link primary"
          >
            Open <FiArrowUpRight size={12} />
          </a>
        ) : null}
        {project.url && (
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className="featured-card-link"
            aria-label={`GitHub: ${project.title}`}
          >
            <FiGithub size={12} /> Source
          </a>
        )}
      </div>
    </article>
  )
}

function ProjectCard({ project }) {
  const { navigate } = useRouter()

  return (
    <div className="project-card">
      <div className="project-content">
        <div className="project-header">
          <h3 className="project-title">
            {project.url || project.demo ? (
              <a
                href={project.url || project.demo}
                target="_blank"
                rel="noopener noreferrer"
              >
                {project.title}
              </a>
            ) : (
              project.title
            )}
          </h3>
          <div className="project-dates">
            <span className="project-date">{project.date}</span>
          </div>
        </div>
        {project.description && (
          <p className="project-description">{project.description}</p>
        )}
        <div className="project-footer">
          {project.technologies?.length > 0 && (
            <span className="project-techs">
              {project.technologies.join(' · ')}
            </span>
          )}
          <div className="project-links">
            {project.detailsIncluded && (
              <a
                href={`/?p=project&slug=${project.slug}`}
                onClick={e => { e.preventDefault(); navigate('project', project.slug) }}
                className="project-link project-link-details"
              >
                Details <FiArrowUpRight size={12} />
              </a>
            )}
            {project.url && (
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <FiGithub size={12} />
                Source
              </a>
            )}
            {project.demo && (
              <a
                href={project.demo}
                target="_blank"
                rel="noopener noreferrer"
                className="project-link"
              >
                <FiExternalLink size={12} />
                Demo
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Projects
